import { SetStateAction, useState } from "react";
import { CreateAuditQuestion } from "./usersView/createAuditQuestions";
import {
  Button,
  Dialog,
  FormControl,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";
import { AuditDialog } from "@mikros/shared-ui";
import { FormattedMessage, useIntl } from "react-intl";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";

export interface AuditTabGroupProps {
  children: React.ReactElement<AuditTabProps>[];
  defaultTabName?: string;
  className?: string;
  tabGroupClassName?: string;
  onClick: (tabName: string) => void;
}

export function AuditTabGroup(props: AuditTabGroupProps) {
  const intl = useIntl();
  const [selectedPage, setSelectedPage] = useState<string>(
    props.defaultTabName ?? ""
  );
  const [showNewAudit, setShowNewAudit] = useState(false);
  const [auditType, setAuditType] = useState("");

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAuditSelection = (event: { target: { value: any } }) => {
    const selectedValue = event.target.value;
    if (
      selectedValue === intl.formatMessage({ id: "audit.auditTabs.newAudit" })
    ) {
      setAuditType("NEW");
    } else if (
      selectedValue ===
      intl.formatMessage({ id: "audit.auditTabs.updateExistingAudit" })
    ) {
      setAuditType("EDIT");
    }
  };

  return (
    <div className={props.className}>
      <div className="flex items-center justify-between mb-4">
     
        <div
          className="inline-flex rounded-full shadow-sm bg-white"
          role="group"
        >
          {props.children.map((tabItem, index) => (
            <button
              type="button"
              className={
                "btn px-4 py-2 text-sm font-semibold rounded-full hover:opacity-80 focus:z-10 " +
                (selectedPage === tabItem.props.name
                  ? "bg-mk-primary-bright text-white shadow-md"
                  : "bg-white text-mk-very-dark-grey btn-secondary border-none")
              }
              onClick={() => {
                setSelectedPage(tabItem.props.name);
                props.onClick(tabItem.props.name);
              }}
              key={index}
            >
              {tabItem.props.title}
            </button>
          ))}
        </div>


        <div className="ml-4 flex items-center relative">
          <Button
            variant="contained"
            disableRipple
            sx={{
              backgroundColor: "var(--color-mk-primary-bright)",
              color: "white",
              border: "1px solid #ccc",
              borderRadius: anchorEl ? "8px 8px 0px 0px" : "8px", // Remove bottom radius when open
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "var(--color-mk-primary-bright)",
                color: "white",
              },
              display: "flex",
              alignItems: "center",
              padding: 0,
              overflow: "hidden",
            }}
          >
            {/* New Audit Text */}
            <div
              className="px-4 py-2"
              onClick={() => {
                setAuditType("NEW");
                setShowNewAudit(true);
              }}
            >
              <FormattedMessage id="audit.auditTabs.newAudit" />
            </div>

            {/* Dropdown Icon Container - Centering the FontAwesome Icon */}
            <div
              className="border-l border-gray-300 px-3 py-2 cursor-pointer flex items-center justify-center"
              style={{ width: "40px", height: "100%" }} // Ensures consistent size
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                setAnchorEl(anchorEl ? null : e.currentTarget as HTMLElement);
              }}
            >
              <FontAwesomeIcon
                icon={["fas", "play" as IconName]} // No need for "fa-" prefix
                className="text-mk-primary transform rotate-90"
                style={{ fontSize: "16px" }} // Adjust size if needed
              />
            </div>
          </Button>

          {anchorEl && (
            <div
              className="absolute left-0 top-full w-full bg-white z-50"
              style={{
                border: "1px solid #ccc",
                borderTop: "none", // Removes separation between button and dropdown
                borderRadius: "0px 0px 8px 8px", // Smooth blending with the rounded button
                boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setAuditType("EDIT");
                  setShowNewAudit(true);
                  setAnchorEl(null);
                }}
                style={{
                  borderRadius: "0px 0px 8px 8px", // Ensuring rounded corners match button
                }}
              >
                <FormattedMessage id="audit.auditTabs.updateExistingAudit" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Section: Render content based on selected tab */}
      <div className="w-full flex-grow">
        {props.children.map((tabItem, index) => (
          <div key={index}>
            {selectedPage === tabItem.props.name && tabItem.props.children}
          </div>
        ))}
      </div>

      {showNewAudit && (
        <Dialog
          PaperProps={{
            style: {
              width: "70vw",
              height: "70vh",
              maxWidth: "90vw", // Override Material UI's default max width
              maxHeight: "70vh", //
              borderRadius: "10px",
              paddingTop: "16px", // equivalent to pt-4
              padding: "20px",
              boxSizing: "border-box",
            },
          }}
          open={true}
          onClose={() => setShowNewAudit(false)}
        >
          <AuditDialog
            component={() => (
              <CreateAuditQuestion
                auditDialogToggle={setShowNewAudit}
                auditDialogValue={showNewAudit}
                auditType={auditType}
              ></CreateAuditQuestion>
            )}
          />
        </Dialog>
      )}
    </div>
  );
}

export interface AuditTabProps {
  name: string;
  title: string;
  children: React.ReactNode;
}

export function AuditTab(props: AuditTabProps) {
  return <div>{props.children}</div>;
}
